import { useState, type FormEvent } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase.js';
import { useCollection, orderBy } from '../../lib/hooks.js';

interface AccountRow {
  id: string;
  name?: string;
  category?: string;
  provider?: string;
  billerUrl?: string;
  status?: string;
}

const CATEGORIES = [
  'credit_card',
  'bank',
  'utility',
  'insurance',
  'mortgage',
  'loan',
  'investment',
  'other',
] as const;

export function AccountsList() {
  const { docs, loading } = useCollection<AccountRow>('accounts', [orderBy('name', 'asc')]);
  const [adding, setAdding] = useState(false);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-6 py-6">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Linked accounts</h1>
          <p className="text-sm text-stone-500">
            Manual accounts for v1. Plaid integration is deferred — see PLAN.md §11.9.
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setAdding(true)}>
          + Add account
        </button>
      </header>

      {adding && <AddAccountForm onDone={() => setAdding(false)} />}

      <div className="card overflow-hidden">
        {loading ? (
          <div className="px-6 py-10 text-center text-sm text-stone-400">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-stone-400">
            No accounts linked yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-stone-200 bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2">Provider</th>
                <th className="px-4 py-2">Biller URL</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((a) => (
                <tr key={a.id} className="border-b border-stone-100">
                  <td className="px-4 py-2 font-medium">{a.name}</td>
                  <td className="px-4 py-2 text-xs">
                    <span className="chip">{a.category}</span>
                  </td>
                  <td className="px-4 py-2 text-xs">{a.provider}</td>
                  <td className="px-4 py-2 text-xs">
                    {a.billerUrl ? (
                      <a
                        href={a.billerUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-stone-700 underline"
                      >
                        {new URL(a.billerUrl).hostname}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    <span className="chip-success">{a.status ?? 'active'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function AddAccountForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('utility');
  const [billerUrl, setBillerUrl] = useState('');
  const [payingEntityId, setPayingEntityId] = useState('');
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await addDoc(collection(db, 'accounts'), {
        name,
        category,
        provider: 'manual',
        billerUrl: billerUrl || undefined,
        payingEntityId: payingEntityId || 'unassigned',
        status: 'active',
        lastSyncedAt: serverTimestamp(),
      });
      onDone();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card space-y-3 p-4" onSubmit={onSubmit}>
      <label className="block text-sm">
        <span className="block pb-1 font-medium text-stone-700">Account name</span>
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded border border-stone-300 px-2 py-1.5"
          placeholder="e.g. Con Edison – 12 Main St"
        />
      </label>
      <label className="block text-sm">
        <span className="block pb-1 font-medium text-stone-700">Category</span>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as (typeof CATEGORIES)[number])}
          className="w-full rounded border border-stone-300 px-2 py-1.5"
        >
          {CATEGORIES.map((c) => (
            <option key={c}>{c.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </label>
      <label className="block text-sm">
        <span className="block pb-1 font-medium text-stone-700">Biller URL (optional)</span>
        <input
          type="url"
          value={billerUrl}
          onChange={(e) => setBillerUrl(e.target.value)}
          className="w-full rounded border border-stone-300 px-2 py-1.5"
          placeholder="https://www.coned.com"
        />
      </label>
      <label className="block text-sm">
        <span className="block pb-1 font-medium text-stone-700">Paying entity ID</span>
        <input
          value={payingEntityId}
          onChange={(e) => setPayingEntityId(e.target.value)}
          className="w-full rounded border border-stone-300 px-2 py-1.5"
          placeholder="(optional, defaults to unassigned)"
        />
      </label>
      <div className="flex gap-2">
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn-secondary" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}
